import { useState, useEffect, useRef } from 'react';
import { searchCities } from '../services/weatherService';

function LocationInput({ onSubmit, onSubmitCity, onUseLocation, isLoading }) {
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const searchRequest = useRef(0);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(debounceRef.current);
      searchRequest.current += 1;
    };
  }, []);

  function handleChange(e) {
    const value = e.target.value;
    setLocation(value);
    setError('');
    setActiveIndex(-1);
    const request = ++searchRequest.current;
    clearTimeout(debounceRef.current);

    // Only search if it looks like a city name (not a ZIP)
    const trimmed = value.trim();
    if (trimmed.length >= 2 && !/^\d+$/.test(trimmed)) {
      debounceRef.current = setTimeout(async () => {
        const results = await searchCities(trimmed).catch(() => []);
        if (request !== searchRequest.current) return;
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }

  function handleSelectSuggestion(suggestion) {
    searchRequest.current += 1;
    clearTimeout(debounceRef.current);
    setLocation(formatSuggestion(suggestion));
    setSuggestions([]);
    setShowSuggestions(false);
    onUseLocation(suggestion.lat, suggestion.lon, suggestion.city);
  }

  function handleKeyDown(e) {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    searchRequest.current += 1;
    clearTimeout(debounceRef.current);

    const trimmed = location.trim();
    if (!trimmed) {
      setError('Please enter a city name or ZIP code');
      return;
    }

    setShowSuggestions(false);

    if (/^\d{5}$/.test(trimmed)) {
      onSubmit(trimmed);
    } else {
      onSubmitCity(trimmed);
    }
  }

  function handleUseLocation() {
    setGeoError('');

    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        onUseLocation(position.coords.latitude, position.coords.longitude);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError('Location access denied. Please enter a city or ZIP code instead.');
        } else {
          setGeoError('Unable to get your location. Please enter a city or ZIP code.');
        }
      }
    );
  }

  function formatSuggestion(s) {
    let label = s.city;
    if (s.state) label += `, ${s.state}`;
    if (s.country) label += `, ${s.country}`;
    return label;
  }

  return (
    <div className="w-full max-w-sm" ref={wrapperRef}>
      <form onSubmit={handleSubmit}>
        <label htmlFor="location" className="block text-stone-400 text-sm mb-2 tracking-wide">
          Which town are you exploring?
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              id="location"
              value={location}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              placeholder="Kingston or 12401"
              className="w-full px-4 py-3 text-lg rounded-xl bg-white border border-stone-200 text-[#2d3b2d] placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-[#2d3b2d]/20 transition-all"
              disabled={isLoading}
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions}
              aria-autocomplete="list"
              aria-controls="city-suggestions"
              aria-activedescendant={activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined}
            />
            {showSuggestions && (
              <ul
                id="city-suggestions"
                role="listbox"
                className="absolute z-50 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden"
              >
                {suggestions.map((s, i) => (
                  <li
                    key={`${s.lat}-${s.lon}`}
                    id={`suggestion-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    onMouseDown={() => handleSelectSuggestion(s)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                      i === activeIndex
                        ? 'bg-[#2d3b2d]/10 text-[#2d3b2d]'
                        : 'text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {formatSuggestion(s)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading || !location.trim()}
            className="px-6 py-3 bg-gradient-to-b from-[#3a4d3a] to-[#2d3b2d] text-white font-medium rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_3px_10px_-3px_rgba(45,59,45,0.45)] hover:from-[#41563f] hover:to-[#33432f] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            {isLoading ? '...' : 'Go'}
          </button>
        </div>
        {error && <p className="mt-2 text-red-500 text-sm">{error}</p>}
      </form>

      <div className="mt-4 text-center">
        <span className="text-stone-300 text-sm">or</span>
      </div>

      <button
        type="button"
        onClick={handleUseLocation}
        disabled={isLoading}
        className="mt-3 w-full px-4 py-3 bg-white border border-stone-200 text-stone-500 font-medium rounded-xl hover:text-[#2d3b2d] hover:border-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        Use my location
      </button>
      {geoError && <p className="mt-2 text-red-500 text-sm text-center">{geoError}</p>}
    </div>
  );
}

export default LocationInput;
